import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  BarChart3, Users, Globe, Monitor, Smartphone, Tablet, Eye, MousePointer,
  Activity, Clock, Loader2, TrendingUp, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function StatCard({ value, label, icon: Icon, color = '#6366f1', sub }) {
  return (
    <div className="border rounded-xl p-4 bg-card">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 shrink-0" style={{ color }} />
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
      {sub && <span className="text-[10px] text-muted-foreground">{sub}</span>}
    </div>
  );
}

function MiniBar({ data, maxVal, color = '#6366f1', height = 40 }) {
  if (!data || data.length === 0) return null;
  const max = maxVal || Math.max(...data.map(d => d.events || d.views || d.visits || 0), 1);
  return (
    <svg width="100%" height={height} className="block">
      {data.map((d, i) => {
        const val = d.events || d.views || d.visits || 0;
        const barH = (val / max) * (height - 4);
        const barW = Math.max(100 / data.length - 1, 2);
        const x = (i / data.length) * 100;
        return <rect key={i} x={`${x}%`} y={height - barH - 2} width={`${barW}%`} height={barH} fill={color} rx="1" opacity="0.7" />;
      })}
    </svg>
  );
}

export default function AnalyticsDashboard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/analytics/overview`, { headers, params: { days } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers, days]);

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return <div className="p-6 text-center text-muted-foreground">No analytics data yet</div>;

  const app = data.app || {};
  const mkt = data.marketing || {};
  const DEVICE_ICONS = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-6xl mx-auto space-y-8" data-testid="analytics-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Analytics</h2>
            <p className="text-xs text-muted-foreground">Page views, feature usage, sessions, and traffic sources.</p>
          </div>
          <div className="flex gap-1 bg-muted/60 rounded-lg p-0.5">
            {[7, 14, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3 py-1 rounded-md text-xs font-medium ${days === d ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* App Analytics */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <Activity className="h-3.5 w-3.5" /> App Usage
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatCard value={app.page_views} label="Page Views" icon={Eye} color="#2563eb" />
            <StatCard value={app.feature_clicks} label="Feature Clicks" icon={MousePointer} color="#7c3aed" />
            <StatCard value={app.sessions} label="Sessions" icon={Activity} color="#059669" />
            <StatCard value={app.unique_users} label="Unique Users" icon={Users} color="#d97706" />
          </div>

          {/* Daily Activity Chart */}
          {(app.daily_activity || []).length > 0 && (
            <div className="border rounded-xl p-4 bg-card mb-4">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Daily Activity</span>
              <MiniBar data={app.daily_activity} color="#6366f1" height={60} />
              <div className="flex justify-between text-[8px] text-muted-foreground mt-1">
                <span>{app.daily_activity[0]?.date}</span>
                <span>{app.daily_activity[app.daily_activity.length - 1]?.date}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Top Pages */}
            {(app.top_pages || []).length > 0 && (
              <div className="border rounded-xl p-4 bg-card">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Top Pages</span>
                <div className="space-y-1.5">
                  {app.top_pages.slice(0, 10).map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground w-4 text-right">#{i + 1}</span>
                      <span className="flex-1 font-medium truncate">{p.page}</span>
                      <span className="font-bold tabular-nums">{p.views}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Features */}
            {(app.top_features || []).length > 0 && (
              <div className="border rounded-xl p-4 bg-card">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Top Features</span>
                <div className="space-y-1.5">
                  {app.top_features.slice(0, 10).map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground w-4 text-right">#{i + 1}</span>
                      <span className="flex-1 font-medium truncate">{f.feature}</span>
                      <span className="font-bold tabular-nums">{f.clicks}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Most Active Users */}
            {(app.top_users || []).length > 0 && (
              <div className="border rounded-xl p-4 bg-card">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Most Active Users</span>
                <div className="space-y-1.5">
                  {app.top_users.slice(0, 10).map((u, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground w-4 text-right">#{i + 1}</span>
                      <span className="flex-1 font-medium truncate">{u.user}</span>
                      <span className="font-bold tabular-nums">{u.events} events</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Device Breakdown */}
            <div className="border rounded-xl p-4 bg-card">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Devices (App)</span>
              <div className="space-y-2">
                {Object.entries(app.device_breakdown || {}).map(([device, count]) => {
                  const Icon = DEVICE_ICONS[device] || Monitor;
                  const total = Object.values(app.device_breakdown).reduce((s, v) => s + v, 0);
                  const pct = total > 0 ? Math.round(count / total * 100) : 0;
                  return (
                    <div key={device} className="flex items-center gap-3 text-xs">
                      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="capitalize w-16">{device}</span>
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="font-bold tabular-nums w-8 text-right">{pct}%</span>
                      <span className="text-muted-foreground tabular-nums w-10 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
              {/* Hourly distribution */}
              {(app.hourly_distribution || []).some(h => h > 0) && (
                <div className="mt-4">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-1">Hourly Distribution</span>
                  <MiniBar data={app.hourly_distribution.map((v, i) => ({ events: v, hour: i }))} color="#0891b2" height={30} />
                  <div className="flex justify-between text-[8px] text-muted-foreground mt-0.5">
                    <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Marketing Analytics */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <Globe className="h-3.5 w-3.5" /> Marketing Site
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            <StatCard value={mkt.total_views} label="Page Views" icon={Eye} color="#2563eb" />
            <StatCard value={mkt.unique_visitors} label="Unique Visitors" icon={Users} color="#059669" />
            <StatCard value={(mkt.traffic_sources || []).length} label="Traffic Sources" icon={ExternalLink} color="#d97706" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Marketing Top Pages */}
            {(mkt.top_pages || []).length > 0 && (
              <div className="border rounded-xl p-4 bg-card">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Top Pages</span>
                <div className="space-y-1.5">
                  {mkt.top_pages.slice(0, 10).map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground w-4 text-right">#{i + 1}</span>
                      <span className="flex-1 font-medium truncate">{p.page}</span>
                      <span className="font-bold tabular-nums">{p.views}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Traffic Sources */}
            {(mkt.traffic_sources || []).length > 0 && (
              <div className="border rounded-xl p-4 bg-card">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Traffic Sources</span>
                <div className="space-y-1.5">
                  {mkt.traffic_sources.slice(0, 10).map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground w-4 text-right">#{i + 1}</span>
                      <span className="flex-1 font-medium truncate">{s.source}</span>
                      <span className="font-bold tabular-nums">{s.visits}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Marketing Daily Chart */}
            {(mkt.daily_activity || []).length > 0 && (
              <div className="border rounded-xl p-4 bg-card">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Daily Traffic</span>
                <MiniBar data={mkt.daily_activity} color="#059669" height={50} />
              </div>
            )}

            {/* Marketing Devices */}
            <div className="border rounded-xl p-4 bg-card">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">Devices (Marketing)</span>
              <div className="space-y-2">
                {Object.entries(mkt.device_breakdown || {}).map(([device, count]) => {
                  const Icon = DEVICE_ICONS[device] || Monitor;
                  const total = Object.values(mkt.device_breakdown).reduce((s, v) => s + v, 0) || 1;
                  const pct = Math.round(count / total * 100);
                  return (
                    <div key={device} className="flex items-center gap-3 text-xs">
                      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="capitalize w-16">{device}</span>
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="font-bold tabular-nums w-8 text-right">{pct}%</span>
                    </div>
                  );
                })}
                {Object.keys(mkt.device_breakdown || {}).length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">No traffic data yet</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tenant Activity */}
        {(app.tenant_activity || []).length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Activity by Tenant</h3>
            <div className="border rounded-xl overflow-hidden">
              <div className="divide-y">
                {app.tenant_activity.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 text-xs">
                    <span className="text-muted-foreground w-4">#{i + 1}</span>
                    <span className="font-medium flex-1">{t.tenant}</span>
                    <span className="font-bold tabular-nums">{t.events} events</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
